"use client";

import { Check } from "lucide-react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CtaButton } from "@/components/optilase/cta-button";
import { headingOn, onSurface, type Surface } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

export type LpTierView = {
  id: string;
  name: string;
  price: string;
  unit?: string;
  monthly?: string;
  summary?: string;
  features: string[];
  highlight: boolean;
  ribbon?: string;
  editable?: Record<string, string>;
};

export default function LpPricingView({
  heading,
  intro,
  tiers,
  ctaLabel,
  footnote,
  surface,
}: {
  heading?: string;
  intro?: string;
  tiers: LpTierView[];
  ctaLabel?: string;
  footnote?: string;
  surface?: Surface | null;
}) {
  if (tiers.length === 0) return null;

  // Three across leaves each card too narrow for the full-size figures.
  const compact = tiers.length >= 3;

  return (
    <div className="mx-auto w-full max-w-7xl">
      {heading && (
        <h2 className={cn("text-section max-w-205", headingOn(surface, "text-teal-08"))}>
          {heading}
        </h2>
      )}
      {intro && (
        <p
          className={cn(
            "mt-4 max-w-230 text-base leading-relaxed lg:text-xl",
            onSurface(surface, "text-white/80", "text-grey-07", "text-grey-07"),
          )}
        >
          {intro}
        </p>
      )}

      <div
        className={cn(
          "mt-10 grid gap-6 lg:mt-14 lg:gap-8",
          tiers.length === 1
            ? "lg:max-w-160"
            : tiers.length > 2
              ? "lg:grid-cols-3"
              : "lg:grid-cols-2",
        )}
      >
        {tiers.map((tier) => (
          <div
            key={tier.id}
            {...(tier.editable ?? {})}
            className={cn(
              "relative flex flex-col gap-6 rounded-3xl p-7 lg:p-10",
              tier.highlight ? "bg-navy-03 text-white" : "bg-white",
            )}
          >
            {tier.ribbon && (
              <span className="bg-brand-orange-02 text-caption absolute -top-3 left-7 rounded-full px-4 py-1.5 font-bold tracking-[0.08em] text-white uppercase lg:left-10">
                {tier.ribbon}
              </span>
            )}

            <div className="flex flex-col gap-2">
              <p
                className={cn(
                  "font-bold",
                  compact ? "text-h6" : "text-h5",
                  tier.highlight ? "text-teal-04" : "text-teal-08",
                )}
              >
                {tier.name}
              </p>
              <p className="flex items-baseline gap-2">
                <span className={cn("leading-none font-bold", compact ? "text-h3" : "text-h2")}>
                  {tier.price}
                </span>
                {tier.unit && (
                  <span
                    className={cn("text-base", tier.highlight ? "text-white/70" : "text-grey-06")}
                  >
                    {tier.unit}
                  </span>
                )}
              </p>
              {tier.monthly && (
                <p
                  className={cn(
                    "text-base font-semibold",
                    tier.highlight ? "text-teal-04" : "text-teal-07",
                  )}
                >
                  {tier.monthly}
                </p>
              )}
              {tier.summary && (
                <p
                  className={cn(
                    "text-base leading-relaxed",
                    tier.highlight ? "text-white/80" : "text-grey-07",
                  )}
                >
                  {tier.summary}
                </p>
              )}
            </div>

            {tier.monthly && (
              <p
                className={cn(
                  "flex flex-wrap items-center gap-2 text-base",
                  tier.highlight ? "text-white/70" : "text-grey-06",
                )}
              >
                Split your payments with
                <AssetImage
                  src={tier.highlight ? "/assets/logo/humm-logo-w.svg" : "/assets/logo/humm.png"}
                  alt="humm"
                  width={77}
                  height={16}
                  className="h-4 w-auto"
                  unoptimized
                />
              </p>
            )}

            <ul className="flex flex-1 flex-col gap-3">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-base leading-relaxed">
                  <Check
                    className={cn(
                      "mt-0.5 size-5 shrink-0",
                      tier.highlight ? "text-teal-05" : "text-teal-07",
                    )}
                    strokeWidth={3}
                  />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <CtaButton
        action="open_booking"
        variant="orange"
        keepOnMobile
        className="mt-8 w-full sm:w-fit lg:mt-10"
      >
        {ctaLabel || "Book consultation"}
      </CtaButton>

      {footnote && (
        <div className="mt-6 flex max-w-215 flex-col gap-2">
          {footnote
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line, i) => (
              <p
                key={i}
                className={cn(
                  "text-sm leading-relaxed",
                  onSurface(surface, "text-white/60", "text-grey-06", "text-grey-06"),
                )}
              >
                {line}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}
